import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  styled,
  CircularProgress,
  Link as MUILink,
  Paper,
} from "@mui/material";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";

// Layout (match Login.js)
const AuthContainer = styled("div")(() => ({
  display: "flex",
  height: "100vh",
}));

const Panel = styled("div")(({ bgColor, textColor }) => ({
  flex: 1,
  backgroundColor: bgColor || "#fff",
  color: textColor || "#000",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  padding: "2rem",
  boxSizing: "border-box",
}));

export default function ResetPassword() {
  console.log("✅ ResetPassword component mounted"); // debug log

  const [ready, setReady] = useState(false);
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [tokenDetected, setTokenDetected] = useState(false);

  // Detect recovery flow (Supabase sets type=recovery in the URL)
  useEffect(() => {
    const hash = typeof window !== "undefined" ? window.location.hash : "";
    if (hash.includes("type=recovery")) setTokenDetected(true);

    // Also listen in case Supabase dispatches PASSWORD_RECOVERY
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") setTokenDetected(true);
    });

    setReady(true);
    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setMsg("");

    if (pwd.length < 8)
      return setErr("Password must be at least 8 characters.");
    if (pwd !== confirm) return setErr("Passwords do not match.");

    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password: pwd });
    setLoading(false);

    if (error) {
      // Most common errors: invalid/expired link if user loaded this page without a valid token
      setErr(error.message || "Could not update password.");
      return;
    }

    // Clean the URL (remove hash params)
    if (typeof window !== "undefined") {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    setMsg("Password updated. You can now sign in.");
  };

  if (!ready) return null;

  return (
    <AuthContainer>
      <Panel>
        <Paper sx={{ p: 3, width: "100%", maxWidth: 420 }}>
          <Typography variant="h5" sx={{ mb: 2, textAlign: "center" }}>
            Set a new password
          </Typography>

          {!tokenDetected && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Open this page from the email link to continue the reset.
            </Alert>
          )}

          {err && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {err}
            </Alert>
          )}
          {msg && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {msg}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              type="password"
              label="New password"
              fullWidth
              sx={{ mb: 2 }}
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              autoFocus
            />
            <TextField
              type="password"
              label="Confirm password"
              fullWidth
              sx={{ mb: 2 }}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              startIcon={loading ? <CircularProgress size={18} /> : null}
            >
              {loading ? "Updating…" : "Update password"}
            </Button>

            <Typography variant="body2" sx={{ mt: 2, textAlign: "center" }}>
              <MUILink component={Link} to="/login">
                Back to sign in
              </MUILink>
            </Typography>
          </Box>
        </Paper>
      </Panel>

      <Panel bgColor="#000000" textColor="#ffffff">
        <Typography variant="h3" sx={{ textAlign: "center" }}>
          Welcome to AISecurityPulse
        </Typography>
      </Panel>
    </AuthContainer>
  );
}
